import {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import type { UserRole } from "./auth-types";
import { CalendarIcon, EditIcon, ExternalLinkIcon, RefreshIcon } from "./icons";
import { ConferenceBrandImage } from "./ConferenceBrandImage";
import { RegistrationCollectionsChart } from "./RegistrationCollectionsChart";
import { ConferenceBrandingPanel } from "./ConferenceBrandingPanel";
import { ConferenceNamesForm } from "./ConferenceNamesForm";
import { localizedConferenceName } from "./conference-names";
import {
  applyConferenceWebsiteUpdate,
  conferenceWebsiteDraftValue,
  effectiveConferenceBrandingUrl,
  normalizeConferenceWebsiteInput,
} from "./conference-branding";
import {
  buildConferenceFinanceSummaryFromAggregate,
  registrationWaitingBreakdown,
  type ConferenceFinanceSummary,
  type InvoiceCollectionStatus,
} from "./conference-finance";
import { WorldVisitorsMap } from "./WorldVisitorsMap";
import { RiyalAmount, RiyalSymbol } from "./RiyalAmount";
import { ConferenceExpenses } from "./ExpensesPanel";
import { OtherRevenuePanel } from "./OtherRevenuePanel";
import type { OtherRevenueRecord } from "./portfolio-overview-api";
import {
  ConferenceBrandingApiError,
  type ConferenceBrandingOverride,
  type ConferenceDashboardClient,
  type ConferenceDetail,
  type ExpenseRecord,
} from "./portfolio-overview-api";
import {
  PORTFOLIO_ACCESS_ERROR_MESSAGES,
  portfolioErrorMessageKey,
  portfolioFailureState,
  type PortfolioFailureState,
} from "./portfolio-api-errors";
import {
  bidiIsolationAttributes,
  isolateLtr,
  isolateText,
  useLocale,
  type LocaleContextValue,
  type MessageKey,
} from "./localization";

export type ConferenceDetailState =
  | { readonly kind: "loading" }
  | { readonly kind: "loaded"; readonly conference: ConferenceDetail }
  | PortfolioFailureState;

export type DetailTab = "overview" | "analytics" | "finance" | "information";

type Translate = LocaleContextValue["t"];
type FormatNumber = LocaleContextValue["number"];
type FormatPercent = LocaleContextValue["percent"];

const invoiceStatusMessageKeys = Object.freeze({
  not_issued: "detail.invoiceStatus.notIssued",
  outstanding: "detail.invoiceStatus.outstanding",
  partially_collected: "detail.invoiceStatus.partiallyCollected",
  collected: "detail.invoiceStatus.collected",
}) satisfies Readonly<Record<InvoiceCollectionStatus, MessageKey>>;

const lifecycleMessageKeys = Object.freeze({
  active: "conference.lifecycle.active",
  upcoming: "conference.lifecycle.upcoming",
  ended: "conference.lifecycle.ended",
}) satisfies Readonly<Record<ConferenceDetail["lifecycle"], MessageKey>>;

const connectionMessageKeys = Object.freeze({
  connected: "conference.connection.connected",
  degraded: "conference.connection.degraded",
  never_synced: "conference.connection.neverSynchronized",
  upgrade_required: "conference.connection.upgradeRequired",
  disabled: "conference.connection.disabled",
}) satisfies Readonly<Record<ConferenceDetail["connectionStatus"], MessageKey>>;

type DataAvailability =
  | ConferenceDetail["dataStatus"]["registration"]
  | ConferenceDetail["dataStatus"]["analytics"];

const dataStatusMessageKeys = Object.freeze({
  available: "detail.dataStatus.available",
  stale: "detail.dataStatus.stale",
  unavailable: "detail.dataStatus.unavailable",
}) satisfies Readonly<Record<DataAvailability, MessageKey>>;

const tabMessageKeys = Object.freeze({
  overview: "detail.tab.overview",
  analytics: "detail.tab.analytics",
  finance: "detail.tab.finance",
  information: "detail.tab.information",
}) satisfies Readonly<Record<DetailTab, MessageKey>>;

function formatCount(
  value: number | null,
  number: FormatNumber,
  t: Translate,
): string {
  return value === null ? t("common.unavailable") : number(value);
}

function formatMoney(value: number | null, currencyCode: string): ReactNode {
  return <RiyalAmount minor={value} currency={currencyCode} />;
}

function formatBasisPoints(
  value: number | null,
  percent: FormatPercent,
  t: Translate,
): string {
  return value === null
    ? t("common.notApplicable")
    : percent(value / 10_000, { maximumFractionDigits: 2 });
}

function financeSummary(
  conference: ConferenceDetail,
  expenseAmountMinor = 0,
  otherRevenueAmountMinor?: number,
): ConferenceFinanceSummary | null {
  const {
    registrationCollectedCents,
    invoicesIssuedCents,
    invoicesOutstandingCents,
    collectedInvoiceCount,
    outstandingInvoiceCount,
    sponsorshipRevenueCents,
    otherRevenueCents,
    netCollectedCents,
  } = conference.finance;

  if (
    registrationCollectedCents === null ||
    invoicesIssuedCents === null ||
    invoicesOutstandingCents === null ||
    collectedInvoiceCount === null ||
    outstandingInvoiceCount === null ||
    netCollectedCents === null
  ) {
    return null;
  }

  try {
    const input = {
      currency: conference.finance.currency,
      registration: {
        collectedAmountMinor: registrationCollectedCents,
        issuedAmountMinor: invoicesIssuedCents,
        outstandingAmountMinor: invoicesOutstandingCents,
        collectedInvoiceCount,
        outstandingInvoiceCount,
      },
      sponsorshipCollectedAmountMinor: sponsorshipRevenueCents,
      otherRevenueCollectedAmountMinor: otherRevenueCents,
      expenseAmountMinor,
    };
    const summary = buildConferenceFinanceSummaryFromAggregate(input);
    if (summary.totalCollectedMinor !== netCollectedCents) return null;
    return otherRevenueAmountMinor === undefined
      ? summary
      : buildConferenceFinanceSummaryFromAggregate({
          ...input,
          otherRevenueCollectedAmountMinor: otherRevenueAmountMinor,
        });
  } catch {
    return null;
  }
}

function TrendChart({ conference }: { readonly conference: ConferenceDetail }) {
  const { date, t } = useLocale();
  const values = conference.trends.map((point) => point.registrationsTotal);
  if (values.length < 2) {
    return (
      <div className="detail-trend-empty">
        <span>{t("detail.trend.title")}</span>
        <p>{t("detail.trend.empty")}</p>
      </div>
    );
  }
  const maximum = Math.max(...values, 1);
  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * 100;
      const y = 50 - (value / maximum) * 44;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <figure className="detail-trend">
      <figcaption>{t("detail.trend.last30Days")}</figcaption>
      <svg
        viewBox="0 0 100 54"
        preserveAspectRatio="none"
        role="img"
        aria-label={t("detail.trend.ariaLabel")}
      >
        <defs>
          <linearGradient id="detail-trend-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--accent)" stopOpacity="0.34" />
            <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon
          points={`0,54 ${points} 100,54`}
          fill="url(#detail-trend-fill)"
        />
        <polyline
          points={points}
          fill="none"
          stroke="var(--accent)"
          strokeWidth="1.1"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="detail-trend__axis">
        <span>
          <bdi>
            {date(conference.trends[0]?.historyDate ?? "", {
              timeZone: "Asia/Riyadh",
              day: "numeric",
              month: "short",
            })}
          </bdi>
        </span>
        <span>
          <bdi>
            {date(conference.trends.at(-1)?.historyDate ?? "", {
              timeZone: "Asia/Riyadh",
              day: "numeric",
              month: "short",
            })}
          </bdi>
        </span>
      </div>
    </figure>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly tone?: "accent" | "warning" | "danger";
}) {
  return (
    <div className="detail-metric">
      <span>{label}</span>
      <strong className={tone ? `detail-metric--${tone}` : undefined}>
        <bdi>{value}</bdi>
      </strong>
    </div>
  );
}

function OverviewTab({
  conference,
}: {
  readonly conference: ConferenceDetail;
}) {
  const { number, t } = useLocale();
  const waiting = registrationWaitingBreakdown(
    conference.registrations,
    conference.finance.outstandingInvoiceCount,
  );
  const formatConferenceMoney = (value: number | null) =>
    formatMoney(value, conference.finance.currency);
  return (
    <div className="detail-overview-grid">
      <div className="detail-overview-main">
        <section className="detail-panel">
          <h2>{t("detail.overview.title")}</h2>
          <div
            className={`detail-registration-grid${waiting && waiting.otherIncomplete > 0 ? " detail-registration-grid--expanded" : ""}`}
          >
            <Metric
              label={t("detail.overview.totalRegistrations")}
              value={formatCount(conference.registrations.total, number, t)}
            />
            <Metric
              label={t("detail.overview.completed")}
              value={formatCount(conference.registrations.completed, number, t)}
              tone="accent"
            />
            <Metric
              label={t(
                waiting
                  ? "detail.overview.awaitingPayment"
                  : "detail.overview.incomplete",
              )}
              value={formatCount(
                waiting?.awaitingPayment ?? conference.registrations.pending,
                number,
                t,
              )}
              tone="warning"
            />
            {waiting && waiting.otherIncomplete > 0 && (
              <Metric
                label={t("detail.overview.otherIncomplete")}
                value={formatCount(waiting.otherIncomplete, number, t)}
              />
            )}
            <Metric
              label={t("detail.overview.cancelled")}
              value={formatCount(conference.registrations.cancelled, number, t)}
              tone="danger"
            />
            <TrendChart conference={conference} />
          </div>
          {waiting && waiting.otherIncomplete > 0 && (
            <p className="detail-registration-note">
              {t("detail.overview.otherIncompleteHint")}
            </p>
          )}
        </section>
        <section className="detail-panel">
          <h2>{t("detail.overview.financialSummary")}</h2>
          <div className="detail-financial-grid">
            <Metric
              label={t("detail.overview.registrationRevenueCollected")}
              value={formatConferenceMoney(
                conference.finance.registrationCollectedCents,
              )}
            />
            <Metric
              label={t("detail.overview.invoicesIssued")}
              value={formatConferenceMoney(
                conference.finance.invoicesIssuedCents,
              )}
            />
            <Metric
              label={t("detail.overview.outstandingInvoices")}
              value={formatConferenceMoney(
                conference.finance.invoicesOutstandingCents,
              )}
              tone="warning"
            />
            <Metric
              label={t("detail.overview.sponsorshipRevenue")}
              value={formatConferenceMoney(
                conference.finance.sponsorshipRevenueCents,
              )}
              tone="accent"
            />
            <Metric
              label={t("detail.overview.otherRevenue")}
              value={formatConferenceMoney(
                conference.finance.otherRevenueCents,
              )}
            />
          </div>
        </section>
      </div>
      <aside className="detail-overview-aside">
        <section className="detail-panel detail-purpose">
          <h2>{t("detail.purpose.title")}</h2>
          <p>
            {conference.objective ? (
              <bdi>{conference.objective}</bdi>
            ) : (
              t("detail.purpose.empty")
            )}
          </p>
        </section>
        <section className="detail-panel">
          <h2>{t("detail.dataStatus.title")}</h2>
          <div className="data-status-list">
            {(
              [
                [
                  "detail.dataStatus.registration",
                  conference.dataStatus.registration,
                ],
                ["detail.dataStatus.payment", conference.dataStatus.payment],
                [
                  "detail.dataStatus.analytics",
                  conference.dataStatus.analytics,
                ],
              ] as const
            ).map(([labelKey, state]) => (
              <div key={labelKey}>
                <span>{t(labelKey)}</span>
                <strong className={`data-state data-state--${state}`}>
                  {t(dataStatusMessageKeys[state])}
                </strong>
              </div>
            ))}
          </div>
        </section>
      </aside>
    </div>
  );
}

function FinanceTab({
  conference,
  client,
  role,
}: {
  readonly conference: ConferenceDetail;
  readonly client: ConferenceDashboardClient;
  readonly role: UserRole;
}) {
  const { number, percent, t } = useLocale();
  const [revenues, setRevenues] = useState<
    readonly OtherRevenueRecord[] | null
  >(null);
  const [revenueStatus, setRevenueStatus] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const handleRevenues = useCallback(
    (records: readonly OtherRevenueRecord[]) => setRevenues(records),
    [],
  );
  const revenueAmountMinor = useMemo(
    () =>
      revenues?.reduce(
        (total, record) =>
          total + (record.status === "active" ? record.amountMinor : 0),
        0,
      ),
    [revenues],
  );
  const [expenses, setExpenses] = useState<readonly ExpenseRecord[] | null>(
    null,
  );
  const [expenseStatus, setExpenseStatus] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const handleExpenses = useCallback(
    (records: readonly ExpenseRecord[]) => setExpenses(records),
    [],
  );
  const expenseAmountMinor = useMemo(
    () =>
      expenses?.reduce(
        (total, record) =>
          total + (record.status === "active" ? record.amountMinor : 0),
        0,
      ) ?? 0,
    [expenses],
  );
  const summary = financeSummary(
    conference,
    expenseAmountMinor,
    revenueStatus === "ready" ? revenueAmountMinor : undefined,
  );
  if (summary === null) {
    return (
      <div className="detail-finance-sections">
        <section
          className="detail-panel finance-unavailable"
          aria-labelledby="finance-unavailable-title"
        >
          <span className="finance-section-kicker">
            {t("detail.finance.integrityKicker")}
          </span>
          <h2 id="finance-unavailable-title">
            {t("detail.finance.unavailableTitle")}
          </h2>
          <p>{t("detail.finance.unavailableDescription")}</p>
        </section>
      </div>
    );
  }

  const invoiceRate = formatBasisPoints(
    summary.invoices.collectionRateBasisPoints,
    percent,
    t,
  );
  const invoiceStatus = t(invoiceStatusMessageKeys[summary.invoices.status]);
  const formatSummaryMoney = (value: number | null) =>
    formatMoney(value, summary.currency);

  return (
    <div className="detail-finance-sections">
      <section
        className="detail-panel finance-position"
        aria-labelledby="finance-position-title"
      >
        <div className="finance-section-heading">
          <div>
            <span className="finance-section-kicker">
              {t("detail.finance.executiveKicker")}
            </span>
            <h2 id="finance-position-title">
              {t("detail.finance.positionTitle")}
            </h2>
          </div>
          <span
            className="finance-currency"
            aria-label={t("detail.finance.currencyAria", {
              currency: isolateLtr(summary.currency),
            })}
          >
            {summary.currency === "SAR" ? (
              <RiyalSymbol />
            ) : (
              <bdi dir="ltr">{summary.currency}</bdi>
            )}{" "}
            · {t("detail.finance.collectedBasis")}
          </span>
        </div>
        <dl className="finance-budget-summary">
          <div data-budget-kind="collected">
            <dt>{t("detail.finance.totalCollected")}</dt>
            <dd>
              {revenueStatus === "ready" ? (
                <bdi>{formatSummaryMoney(summary.totalCollectedMinor)}</bdi>
              ) : (
                t(
                  revenueStatus === "error"
                    ? "otherRevenue.loadFailed"
                    : "otherRevenue.loading",
                )
              )}
            </dd>
          </div>
          <div data-budget-kind="pending">
            <dt>{t("detail.finance.pendingCollection")}</dt>
            <dd>
              <bdi>
                {formatSummaryMoney(summary.invoices.outstandingAmountMinor)}
              </bdi>
            </dd>
          </div>
          <div data-budget-kind="expenses">
            <dt>{t("detail.finance.totalExpenses")}</dt>
            <dd>
              {expenseStatus !== "ready" ? (
                t(
                  expenseStatus === "error"
                    ? "expense.unavailableShort"
                    : "expense.loading",
                )
              ) : (
                <bdi>{formatSummaryMoney(summary.expenseMinor)}</bdi>
              )}
            </dd>
          </div>
          <div
            data-budget-kind={
              summary.remainingMinor < 0 ? "deficit" : "remaining"
            }
          >
            <dt>
              {summary.remainingMinor < 0
                ? t("detail.finance.deficit")
                : t("detail.finance.availableBalance")}
            </dt>
            <dd>
              {revenueStatus !== "ready" ? (
                t(
                  revenueStatus === "error"
                    ? "otherRevenue.loadFailed"
                    : "otherRevenue.loading",
                )
              ) : expenseStatus !== "ready" ? (
                t(
                  expenseStatus === "error"
                    ? "expense.unavailableShort"
                    : "expense.loading",
                )
              ) : (
                <bdi>{formatSummaryMoney(summary.remainingMinor)}</bdi>
              )}
            </dd>
          </div>
        </dl>
        <p className="finance-section-description">
          {t("detail.finance.cashReceivedDescription")}
        </p>
      </section>

      <section
        className="detail-panel finance-revenue"
        aria-labelledby="finance-revenue-title"
      >
        <div className="finance-section-heading">
          <div>
            <span className="finance-section-kicker">
              {t("detail.finance.componentsKicker")}
            </span>
            <h2 id="finance-revenue-title">
              {t("detail.finance.revenueMixTitle")}
            </h2>
          </div>
        </div>
        <p className="finance-section-description">
          {t("detail.finance.revenueMixDescription")}
        </p>
        <dl className="finance-revenue-mix">
          <div data-revenue-kind="registration">
            <dt>{t("detail.finance.registrationCollected")}</dt>
            <dd>
              <bdi>
                {formatSummaryMoney(summary.registrationCollectedMinor)}
              </bdi>
            </dd>
          </div>
          <div data-revenue-kind="sponsorship">
            <dt>{t("detail.finance.sponsorshipCollected")}</dt>
            <dd>
              <bdi>{formatSummaryMoney(summary.sponsorshipCollectedMinor)}</bdi>
            </dd>
          </div>
          <div data-revenue-kind="other">
            <dt>{t("detail.finance.otherRevenueCollected")}</dt>
            <dd>
              <bdi>
                {revenueStatus === "ready"
                  ? formatSummaryMoney(summary.otherRevenueCollectedMinor)
                  : t(
                      revenueStatus === "error"
                        ? "otherRevenue.loadFailed"
                        : "otherRevenue.loading",
                    )}
              </bdi>
            </dd>
          </div>
        </dl>
      </section>

      <section
        className="detail-panel finance-invoices"
        aria-labelledby="finance-invoices-title"
      >
        <div className="finance-section-heading">
          <div>
            <span className="finance-section-kicker">
              {t("detail.finance.registrationOnlyKicker")}
            </span>
            <h2 id="finance-invoices-title">
              {t("detail.finance.invoiceStatusTitle")}
            </h2>
          </div>
          <span
            className={`finance-invoice-status finance-invoice-status--${summary.invoices.status}`}
          >
            {invoiceStatus}
          </span>
        </div>
        <div className="finance-invoice-layout">
          <div className="finance-invoice-rate">
            <div className="finance-invoice-rate__heading">
              <span>{t("detail.finance.collectionRate")}</span>
              <strong>
                <bdi>{invoiceRate}</bdi>
              </strong>
            </div>
            {summary.invoices.collectionRateBasisPoints === null ? (
              <p>{t("detail.finance.noInvoicesIssued")}</p>
            ) : (
              <progress
                max={10_000}
                value={summary.invoices.collectionRateBasisPoints}
                aria-label={t("detail.finance.collectionRateAria")}
                aria-valuetext={invoiceRate}
              >
                {invoiceRate}
              </progress>
            )}
          </div>
          <dl className="finance-invoice-facts">
            <div>
              <dt>{t("detail.finance.invoiceValueIssued")}</dt>
              <dd>
                <bdi>
                  {formatSummaryMoney(summary.invoices.issuedAmountMinor)}
                </bdi>
              </dd>
            </div>
            <div>
              <dt>{t("detail.finance.outstandingBalance")}</dt>
              <dd>
                <bdi>
                  {formatSummaryMoney(summary.invoices.outstandingAmountMinor)}
                </bdi>
              </dd>
            </div>
          </dl>
        </div>
        <dl className="finance-invoice-counts">
          <div>
            <dt>{t("detail.finance.invoicesIssued")}</dt>
            <dd>
              <bdi>
                {formatCount(summary.invoices.issuedInvoiceCount, number, t)}
              </bdi>
            </dd>
          </div>
          <div>
            <dt>{t("detail.finance.invoicesCollected")}</dt>
            <dd>
              <bdi>
                {formatCount(summary.invoices.collectedInvoiceCount, number, t)}
              </bdi>
            </dd>
          </div>
          <div>
            <dt>{t("detail.finance.invoicesOutstanding")}</dt>
            <dd>
              <bdi>
                {formatCount(
                  summary.invoices.outstandingInvoiceCount,
                  number,
                  t,
                )}
              </bdi>
            </dd>
          </div>
        </dl>
        <p className="finance-invoice-note">
          {t("detail.finance.invoiceNote")}
        </p>
      </section>

      <RegistrationCollectionsChart
        key={conference.conferenceId}
        data={conference.finance.registrationCollections}
        stale={conference.freshness !== "fresh"}
      />

      <section
        className="detail-panel finance-expenses"
        aria-label={t("detail.finance.expenseDetails")}
      >
        <ConferenceExpenses
          conferenceId={conference.conferenceId}
          client={client}
          canManage={false}
          canEdit={role === "admin"}
          compact
          onRecordsChange={handleExpenses}
          onAvailabilityChange={setExpenseStatus}
        />
      </section>
      <section
        className="detail-panel finance-other-revenue"
        aria-label={t("otherRevenue.conferenceHeading")}
      >
        <OtherRevenuePanel
          key={`finance-revenue-${conference.conferenceId}`}
          conferenceId={conference.conferenceId}
          client={client}
          canManage={false}
          canEdit={role === "admin"}
          compact
          onRecordsChange={handleRevenues}
          onAvailabilityChange={setRevenueStatus}
        />
      </section>
    </div>
  );
}

export function ConferenceWebsiteForm({
  client,
  conference,
  onSaved,
}: {
  readonly client: ConferenceDashboardClient;
  readonly conference: ConferenceDetail;
  readonly onSaved: (websiteUrl: string | null) => void;
}) {
  const { t } = useLocale();
  const [websiteUrl, setWebsiteUrl] = useState(() =>
    conferenceWebsiteDraftValue(conference),
  );
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<
    "idle" | "saved" | "reset" | "error" | "invalid"
  >("idle");
  const [failureKey, setFailureKey] = useState<MessageKey>(
    "conferenceWebsite.error",
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const dirty = websiteUrl.trim() !== conferenceWebsiteDraftValue(conference);

  useEffect(() => {
    if (status === "invalid" && !busy) inputRef.current?.focus();
  }, [status, busy]);

  useEffect(() => {
    if (!dirty) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [dirty]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const normalized = normalizeConferenceWebsiteInput(websiteUrl);
    if (normalized === undefined) {
      setStatus("invalid");
      inputRef.current?.focus();
      return;
    }
    if (!client.updateWebsite) {
      setFailureKey("conferenceWebsite.error");
      setStatus("error");
      return;
    }
    setBusy(true);
    setStatus("idle");
    try {
      const saved = await client.updateWebsite(
        conference.conferenceId,
        normalized,
      );
      setWebsiteUrl(saved ?? "");
      onSaved(saved);
      setStatus(saved === null ? "reset" : "saved");
    } catch (error) {
      if (
        error instanceof ConferenceBrandingApiError &&
        error.code === "invalid_conference_website"
      ) {
        setStatus("invalid");
        inputRef.current?.focus();
      } else {
        setFailureKey(
          portfolioErrorMessageKey(error, "conferenceWebsite.error"),
        );
        setStatus("error");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="detail-panel conference-names"
      aria-labelledby="conference-website-title"
    >
      <h2 id="conference-website-title">{t("conferenceWebsite.title")}</h2>
      <p id="conference-website-help">{t("conferenceWebsite.description")}</p>
      <form onSubmit={save} noValidate>
        <fieldset disabled={busy}>
          <div
            className="conference-names__fields"
            style={{ gridTemplateColumns: "minmax(0, 1fr)" }}
          >
            <label htmlFor="conference-website-url">
              {t("conferenceWebsite.label")}
              <input
                ref={inputRef}
                id="conference-website-url"
                name="websiteUrl"
                type="url"
                inputMode="url"
                dir="ltr"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                maxLength={2048}
                value={websiteUrl}
                aria-invalid={status === "invalid" || undefined}
                aria-describedby="conference-website-help conference-website-status"
                onChange={(event) => {
                  setWebsiteUrl(event.target.value);
                  setStatus("idle");
                }}
              />
            </label>
          </div>
          <p>
            {t("conferenceWebsite.default")}{" "}
            <bdi dir="ltr">{`https://${conference.projectId}.web.app`}</bdi>
          </p>
          <div className="conference-names__actions">
            <button
              className="button button--primary"
              type="submit"
              disabled={!dirty || !client.updateWebsite}
            >
              {t(busy ? "conferenceEditor.saving" : "conferenceWebsite.save")}
            </button>
            <button
              className="button button--secondary"
              type="button"
              disabled={!dirty}
              onClick={() => {
                setWebsiteUrl(conferenceWebsiteDraftValue(conference));
                setStatus("idle");
              }}
            >
              {t("conferenceEditor.cancel")}
            </button>
          </div>
        </fieldset>
        <p
          id="conference-website-status"
          role={status === "error" || status === "invalid" ? "alert" : "status"}
        >
          {status === "error"
            ? t(failureKey)
            : status !== "idle"
              ? t(`conferenceWebsite.${status}`)
              : dirty
                ? t("conferenceEditor.unsaved")
                : ""}
        </p>
      </form>
    </section>
  );
}

function InformationTab({
  client,
  conference,
  role,
  onBrandingUpdated,
  onNamesUpdated,
  onWebsiteUpdated,
}: {
  readonly client: ConferenceDashboardClient;
  readonly conference: ConferenceDetail;
  readonly role: UserRole;
  readonly onWebsiteUpdated?: (websiteUrl: string | null) => void;
  readonly onNamesUpdated?: (
    names: NonNullable<ConferenceDetail["displayNames"]>,
  ) => void;
  readonly onBrandingUpdated: (
    brandingOverride: ConferenceBrandingOverride | null,
  ) => void;
}) {
  const { date, t } = useLocale();
  const dateTimeOptions = {
    timeZone: "Asia/Riyadh",
    dateStyle: "medium",
    timeStyle: "short",
  } as const;
  return (
    <div className="detail-information-view">
      {role === "admin" ? (
        <ConferenceNamesForm
          key={conference.conferenceId}
          client={client}
          conference={conference}
          onSaved={(names) => onNamesUpdated?.(names)}
        />
      ) : null}
      {role === "admin" ? (
        <ConferenceWebsiteForm
          key={`website-${conference.conferenceId}`}
          client={client}
          conference={conference}
          onSaved={(websiteUrl) => onWebsiteUpdated?.(websiteUrl)}
        />
      ) : null}
      {role === "admin" ? (
        <ConferenceBrandingPanel
          client={client}
          conference={conference}
          role={role}
          onBrandingUpdated={onBrandingUpdated}
        />
      ) : null}
      <div className="detail-information-grid">
        <section className="detail-panel">
          <h2>{t("detail.information.title")}</h2>
          <dl className="detail-definition-list">
            <div>
              <dt>{t("detail.information.conference")}</dt>
              <dd>
                <bdi>{conference.name}</bdi>
              </dd>
            </div>
            <div>
              <dt>{t("detail.information.firebaseProject")}</dt>
              <dd>
                <bdi {...bidiIsolationAttributes("ltr")}>
                  {conference.projectId}
                </bdi>
              </dd>
            </div>
            <div>
              <dt>{t("detail.information.starts")}</dt>
              <dd>
                <bdi>{date(conference.startsAt, dateTimeOptions)}</bdi>
              </dd>
            </div>
            <div>
              <dt>{t("detail.information.ends")}</dt>
              <dd>
                <bdi>{date(conference.endsAt, dateTimeOptions)}</bdi>
              </dd>
            </div>
            <div>
              <dt>{t("detail.information.connection")}</dt>
              <dd>{t(connectionMessageKeys[conference.connectionStatus])}</dd>
            </div>
            <div>
              <dt>{t("detail.information.lastSynchronized")}</dt>
              <dd>
                {conference.lastSuccessfulSyncAt ? (
                  <bdi>
                    {date(conference.lastSuccessfulSyncAt, dateTimeOptions)}
                  </bdi>
                ) : (
                  t("sync.notSynchronized")
                )}
              </dd>
            </div>
          </dl>
        </section>
        <section className="detail-panel detail-purpose">
          <h2>{t("detail.purpose.title")}</h2>
          <p>
            {conference.objective ? (
              <bdi>{conference.objective}</bdi>
            ) : (
              t("detail.purpose.empty")
            )}
          </p>
          <a
            className="button button--secondary detail-website-link"
            href={conference.websiteUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={t("detail.website.ariaLabel", {
              conference: isolateText(conference.name),
            })}
          >
            {t("detail.website.open")} <span aria-hidden="true">↗</span>
          </a>
        </section>
      </div>
      {role !== "admin" ? (
        <ConferenceBrandingPanel
          client={client}
          conference={conference}
          role={role}
          onBrandingUpdated={onBrandingUpdated}
        />
      ) : null}
    </div>
  );
}

function DailyVisitorsChart({
  points,
}: {
  readonly points: ConferenceDetail["analytics"]["dailyVisitors"];
}) {
  const { date, t } = useLocale();
  if (points.length < 2) {
    return (
      <div className="analytics-chart analytics-chart--empty">
        <h2>{t("detail.analytics.dailyVisitors")}</h2>
        <p>{t("detail.analytics.dailyEmpty")}</p>
      </div>
    );
  }
  const maximum = Math.max(...points.map((point) => point.visitors), 1);
  const line = points
    .map((point, index) => {
      const x = 4 + (index / (points.length - 1)) * 92;
      const y = 48 - (point.visitors / maximum) * 42;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <figure className="analytics-chart">
      <figcaption>{t("detail.analytics.dailyVisitors")}</figcaption>
      <svg
        viewBox="0 0 100 52"
        preserveAspectRatio="none"
        role="img"
        aria-label={t("detail.analytics.dailyAria")}
      >
        <defs>
          <linearGradient id="analytics-trend-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--accent)" stopOpacity="0.28" />
            <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon
          points={`4,52 ${line} 96,52`}
          fill="url(#analytics-trend-fill)"
        />
        <polyline
          points={line}
          fill="none"
          stroke="var(--accent)"
          strokeWidth="1.25"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="analytics-chart__axis">
        <span>
          <bdi>
            {date(points[0]?.date ?? "", {
              timeZone: "Asia/Riyadh",
              day: "numeric",
              month: "short",
            })}
          </bdi>
        </span>
        <span>
          <bdi>
            {date(points.at(-1)?.date ?? "", {
              timeZone: "Asia/Riyadh",
              day: "numeric",
              month: "short",
            })}
          </bdi>
        </span>
      </div>
    </figure>
  );
}

function AnalyticsTab({
  conference,
}: {
  readonly conference: ConferenceDetail;
}) {
  const { locale, number, date, t } = useLocale();
  const countryNames = useMemo(
    () => new Intl.DisplayNames([locale], { type: "region" }),
    [locale],
  );
  const analytics = conference.analytics;
  const lifetime = analytics.lifetime;
  const available = conference.dataStatus.analytics !== "unavailable";
  const topCountries = [...analytics.countries]
    .sort((left, right) => right.visitors - left.visitors)
    .slice(0, 7);
  if (!available) {
    return (
      <section className="analytics-unavailable" role="status">
        <span aria-hidden="true">◎</span>
        <h2>{t("detail.analytics.unavailableTitle")}</h2>
        <p>{t("detail.analytics.unavailableDescription")}</p>
      </section>
    );
  }
  return (
    <div className="analytics-view">
      <section
        className="analytics-lifetime"
        aria-labelledby="analytics-lifetime-title"
      >
        <div>
          <h2 id="analytics-lifetime-title">
            {t("detail.analytics.totalUsers")}
          </h2>
          <p>
            {lifetime?.availability === "available"
              ? t("detail.analytics.trackingPeriod", {
                  start: date(`${lifetime.startDate}T12:00:00Z`, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  }),
                  end: date(`${lifetime.endDate}T12:00:00Z`, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  }),
                })
              : t("detail.analytics.lifetimeUnavailable")}
          </p>
          <small>{t("detail.analytics.lifetimeNote")}</small>
        </div>
        <strong>
          <bdi dir="ltr">
            {lifetime?.availability === "available"
              ? number(lifetime.totalUsers)
              : "—"}
          </bdi>
        </strong>
      </section>
      <section aria-labelledby="audience-overview-title">
        <h2 id="audience-overview-title">
          {t("detail.analytics.audienceTitle")}
        </h2>
        <div className="analytics-metric-rail">
          <Metric
            label={t("detail.analytics.today")}
            value={formatCount(analytics.activeUsersToday, number, t)}
            tone="accent"
          />
          <Metric
            label={t("detail.analytics.yesterday")}
            value={formatCount(analytics.activeUsersYesterday, number, t)}
          />
          <Metric
            label={t("detail.analytics.thisMonth")}
            value={formatCount(analytics.activeUsersThisMonth, number, t)}
            tone="accent"
          />
          <Metric
            label={t("detail.analytics.last30Minutes")}
            value={formatCount(analytics.activeUsersLast30Minutes, number, t)}
          />
        </div>
        <p className="analytics-estimate-note">
          <span aria-hidden="true">ⓘ</span> {t("detail.analytics.estimateNote")}
        </p>
      </section>
      <div className="analytics-grid">
        <WorldVisitorsMap countries={analytics.countries} />
        <div className="analytics-grid__side">
          <section className="analytics-countries">
            <h2>{t("detail.analytics.topCountries")}</h2>
            {topCountries.length ? (
              <ol>
                {topCountries.map((country) => (
                  <li key={country.countryCode}>
                    <span>
                      <bdi>
                        {countryNames.of(country.countryCode) ??
                          country.countryCode}
                      </bdi>
                    </span>
                    <strong>
                      <bdi>{number(country.visitors)}</bdi>
                    </strong>
                  </li>
                ))}
              </ol>
            ) : (
              <p>{t("detail.analytics.noCountries")}</p>
            )}
          </section>
          <DailyVisitorsChart points={analytics.dailyVisitors} />
        </div>
      </div>
    </div>
  );
}

export function ConferenceDetailView({
  client,
  state,
  tab,
  role,
  onTabChange,
  onRetry,
  onBrandingUpdated,
  onNamesUpdated,
  onWebsiteUpdated,
}: {
  readonly client: ConferenceDashboardClient;
  readonly state: ConferenceDetailState;
  readonly tab: DetailTab;
  readonly role: UserRole;
  readonly onTabChange: (tab: DetailTab) => void;
  readonly onRetry: () => void;
  readonly onWebsiteUpdated?: (websiteUrl: string | null) => void;
  readonly onNamesUpdated?: (
    names: NonNullable<ConferenceDetail["displayNames"]>,
  ) => void;
  readonly onBrandingUpdated: (
    brandingOverride: ConferenceBrandingOverride | null,
  ) => void;
}) {
  const { date, dateRange, locale, t } = useLocale();
  const dateTimeOptions = {
    timeZone: "Asia/Riyadh",
    dateStyle: "medium",
    timeStyle: "short",
  } as const;
  if (state.kind === "loading") {
    return (
      <div
        className="detail-loading"
        aria-label={t("detail.loading")}
        aria-busy="true"
      >
        <span />
        <span />
        <span />
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <section className="overview-state" role="alert">
        <h2>
          {state.sessionExpired
            ? t("session.expired.title")
            : state.accessError
              ? t("access.denied.title")
              : t("detail.error.title")}
        </h2>
        <p>
          {state.sessionExpired
            ? t("session.expired.description")
            : state.accessError
              ? t(PORTFOLIO_ACCESS_ERROR_MESSAGES[state.accessError])
              : t("detail.error.description")}
        </p>
        {!state.sessionExpired && !state.accessError ? (
          <button
            className="button button--secondary overview-state__action"
            type="button"
            onClick={onRetry}
          >
            <RefreshIcon />
            {t("common.tryAgain")}
          </button>
        ) : null}
      </section>
    );
  }
  const conference = {
    ...state.conference,
    name: localizedConferenceName(state.conference, locale),
  };
  return (
    <>
      <div className="detail-breadcrumbs">
        <a href="#/conferences">{t("detail.breadcrumb.conferences")}</a>
        <span aria-hidden="true">/</span>
        <span>
          <bdi>{conference.name}</bdi>
        </span>
      </div>
      <header className="conference-detail-header">
        <ConferenceBrandImage
          conferenceId={conference.conferenceId}
          conferenceName={conference.name}
          configuredLogo={effectiveConferenceBrandingUrl(conference, "logo")}
          fallbackClassName="conference-detail-header__monogram"
          width="112"
          height="112"
        />
        <div>
          <div className="conference-detail-header__title">
            <h1>
              <bdi>{conference.name}</bdi>
            </h1>
            <span>{t(lifecycleMessageKeys[conference.lifecycle])}</span>
          </div>
          <p className="conference-detail-header__date">
            <CalendarIcon />
            <bdi>
              {dateRange(conference.startsAt, conference.endsAt, {
                timeZone: "Asia/Riyadh",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </bdi>
          </p>
        </div>
        <div className="conference-detail-header__actions">
          <div className="conference-detail-header__links">
            {role === "admin" ? (
              <a
                className="conference-detail-action"
                href={`#/conferences/${encodeURIComponent(conference.conferenceId)}/information`}
                onClick={() => onTabChange("information")}
              >
                <EditIcon />
                {t("conferenceEditor.open")}
              </a>
            ) : null}
            <a
              className="conference-detail-action"
              href={conference.websiteUrl}
              target="_blank"
              rel="noreferrer"
              aria-label={t("detail.website.ariaLabel", {
                conference: isolateText(conference.name),
              })}
            >
              <ExternalLinkIcon />
              {t("detail.website.open")}
            </a>
          </div>
          <span>
            {conference.lastSuccessfulSyncAt
              ? t("detail.lastSynchronized", {
                  date: isolateText(
                    date(conference.lastSuccessfulSyncAt, dateTimeOptions),
                  ),
                })
              : t("sync.notSynchronized")}
          </span>
        </div>
      </header>
      {conference.connectionStatus === "upgrade_required" ? (
        <section
          className="detail-connection-notice detail-connection-notice--upgrade"
          role="status"
        >
          <strong>{t("detail.notice.upgradeTitle")}</strong>
          <span>{t("detail.notice.upgradeDescription")}</span>
        </section>
      ) : conference.freshness === "stale" ||
        conference.connectionStatus === "degraded" ? (
        <section className="detail-connection-notice" role="status">
          <strong>{t("detail.notice.delayedTitle")}</strong>
          <span>{t("detail.notice.delayedDescription")}</span>
        </section>
      ) : null}
      <nav className="detail-tabs" aria-label={t("detail.tabs.ariaLabel")}>
        {(["overview", "analytics", "finance", "information"] as const).map(
          (value) => (
            <a
              key={value}
              href={`#/conferences/${encodeURIComponent(conference.conferenceId)}${
                value === "overview" ? "" : `/${value}`
              }`}
              aria-current={tab === value ? "page" : undefined}
              onClick={() => onTabChange(value)}
            >
              {t(tabMessageKeys[value])}
            </a>
          ),
        )}
      </nav>
      {tab === "overview" ? (
        <OverviewTab conference={conference} />
      ) : tab === "analytics" ? (
        <AnalyticsTab conference={conference} />
      ) : tab === "finance" ? (
        <FinanceTab conference={conference} client={client} role={role} />
      ) : (
        <InformationTab
          client={client}
          conference={conference}
          role={role}
          onBrandingUpdated={onBrandingUpdated}
          onNamesUpdated={(names) => onNamesUpdated?.(names)}
          onWebsiteUpdated={(websiteUrl) => onWebsiteUpdated?.(websiteUrl)}
        />
      )}
    </>
  );
}

export function ConferenceDetailPanel({
  client,
  conferenceId,
  role,
  initialTab = "overview",
}: {
  readonly client: ConferenceDashboardClient;
  readonly conferenceId: string;
  readonly role: UserRole;
  readonly initialTab?: DetailTab;
}) {
  const [state, setState] = useState<ConferenceDetailState>({
    kind: "loading",
  });
  const [tab, setTab] = useState<DetailTab>("overview");
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const abortController = new AbortController();
    setState({ kind: "loading" });
    setTab(initialTab);
    void client
      .loadConferenceDetail(conferenceId, abortController.signal)
      .then((conference) => setState({ kind: "loaded", conference }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        setState(portfolioFailureState(error));
      });
    return () => abortController.abort();
  }, [client, conferenceId, initialTab, revision]);

  return (
    <ConferenceDetailView
      client={client}
      state={state}
      tab={tab}
      role={role}
      onTabChange={setTab}
      onRetry={() => setRevision((value) => value + 1)}
      onWebsiteUpdated={(websiteUrl) =>
        setState((current) =>
          current.kind === "loaded" &&
          current.conference.conferenceId === conferenceId
            ? {
                ...current,
                conference: applyConferenceWebsiteUpdate(
                  current.conference,
                  websiteUrl,
                ),
              }
            : current,
        )
      }
      onNamesUpdated={(displayNames) =>
        setState((current) =>
          current.kind === "loaded"
            ? {
                ...current,
                conference: { ...current.conference, displayNames },
              }
            : current,
        )
      }
      onBrandingUpdated={(brandingOverride) => {
        setState((current) =>
          current.kind === "loaded"
            ? {
                kind: "loaded",
                conference: {
                  ...current.conference,
                  brandingOverride,
                  effectiveIconUrl:
                    brandingOverride?.icon?.url ??
                    current.conference.iconUrl ??
                    current.conference.logoUrl,
                  effectiveLogoUrl:
                    brandingOverride?.logo?.url ?? current.conference.logoUrl,
                },
              }
            : current,
        );
      }}
    />
  );
}
